import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments';
import { Observable, throwError as _throw } from 'rxjs';
import { map, catchError, debounceTime } from 'rxjs/operators';
import { DueDiligenceOutgoingRequestItemFilter } from '../models/due-diligence/due-diligence-outgoing-request-item-filter';
import { Priority } from '../models/priority';
import { Level } from '../models/level';
import { ItemStatus } from '../models/item-status';
import { DueDiligenceIncomingRequestItemFilter } from '../models/due-diligence/due-diligence-incoming-request-item-filter';
import { DueDiligenceProvider } from '../models/due-diligence/due-diligence-provider';
import { Country } from '../models/country';
import { DueDiligenceStatusCount } from '../models/due-diligence/due-diligence-status.count';
import { DueDiligenceThirdParty } from '../models/due-diligence/due-diligence-thirdparty';
import { DueDiligenceThirdPartyDetail } from '../models/due-diligence/due-diligence-thirdparty-detail';
import { DueDiligenceDocumentType } from '../models/due-diligence/due-diligence-document-type';
import { LocationCountry } from '../models/location-country';
import { LocationState } from '../models/location-state';
import { LocationCity } from '../models/location-city';
import { DueDiligenceRequest } from '../models/due-diligence/due-diligence-request';
import { ThirdPartyEvaluationFormAnswer } from '../models/thirdparty-evaluation-form-answer';
import { ThirdPartyEvaluationFormItemFile } from '../models/thirdparty-evaluation-form-item-file';
import { Culture } from '../models/culture/culture.table';
import { AuthService } from '../core/auth/_services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class DueDiligenceService {
  private apiEndPoint: string;

  constructor(
    private _http: HttpClient,
    private _translate: TranslateService,
    private _auth: AuthService,
  ) {
    this.apiEndPoint = environment.apiUrl;
  }

  getPagedOutgoingRequests(outgoingRequestsFilter: DueDiligenceOutgoingRequestItemFilter): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/DueDiligence/GetDueDiligenceListPagedFilter`, outgoingRequestsFilter)
      .pipe(
        debounceTime(5000),
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getPagedIncomingRequests(outgoingRequestsFilter: DueDiligenceIncomingRequestItemFilter): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/DueDiligence/GetDueDiligenceListForProviderPagedFilter`, outgoingRequestsFilter)
      .pipe(
        debounceTime(5000),
        map(res => res),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getDefaultProviderForThirdParty(thirdPartyId: number): Observable<DueDiligenceProvider> {
    const params = new HttpParams()
      .append("thridPartyId", thirdPartyId.toString());

    return this._http
      .get<any>(`${this.apiEndPoint}/DueDiligence/GetDefaultProvider`, { params })
      .pipe(
        map(res => {
          return {
            id: res.id,
            description: res.name
          };
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  sendRequest(request: DueDiligenceRequest): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/DueDiligence/GenerateUpdateDueDiligence`, request)
      .pipe(
        map(res => res),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestPriorities(): Observable<Priority[]> {
    return this._http
      .get<Priority[]>(`${this.apiEndPoint}/DueDiligence/GetPriorities`)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestLevel(): Observable<Level[]> {
    return this._http
      .get<Level[]>(`${this.apiEndPoint}/DueDiligence/GetDueDiligenceLevels`)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestStatus(): Observable<ItemStatus[]> {
    return this._http
      .get<any[]>(`${this.apiEndPoint}/DueDiligence/GetDueDiligenceStatus`)
      .pipe(
        map(res => res),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getProviders(): Observable<DueDiligenceProvider[]> {
    return this._http
      .get<any[]>(`${this.apiEndPoint}/DueDiligence/GetDueDiligenceProviders`)
      .pipe(
        map(res => res.map(x => {
          return {
            id: x.id,
            description: x.name
          }
        })),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getCountries(): Observable<Country[]> {
    return this._http
      .get<any[]>(`${this.apiEndPoint}/DueDiligence/GetCountries`)
      .pipe(
        map(res => res.map(x => {
          return {
            id: x.id,
            description: x.name
          }
        })),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getStatusCount(): Observable<DueDiligenceStatusCount> {
    return this._http
      .get<DueDiligenceStatusCount>(`${this.apiEndPoint}/DueDiligence/GetDueDiligenceStatusCount`)
      .pipe(
        map(res => res),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestThirdParties(name: string): Observable<DueDiligenceThirdParty[]> {
    const params = new HttpParams()
      .append("name", name);

    return this._http
      .get<DueDiligenceThirdParty[]>(`${this.apiEndPoint}/DueDiligence/GetThirdParties`, { params })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestThirdPartyDetail(thirdPartyId: number): Observable<DueDiligenceThirdPartyDetail> {
    return this._http
      .get<DueDiligenceThirdPartyDetail>(`${this.apiEndPoint}/DueDiligence/GetDetailOfThirdParty/${thirdPartyId}`)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestDocumentTypes(): Observable<DueDiligenceDocumentType[]> {
    return this._http
      .get<DueDiligenceDocumentType[]>(`${this.apiEndPoint}/DueDiligence/GetDocumentTypes`)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestCountries(): Observable<LocationCountry[]> {
    return this._http
      .get<LocationCountry[]>(`${this.apiEndPoint}/DueDiligence/GetCountries`)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestStates(countryId: number): Observable<LocationState[]> {
    return this._http
      .get<LocationState[]>(`${this.apiEndPoint}/DueDiligence/GetStates/${countryId}`)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestCities(stateId: number): Observable<LocationCity[]> {
    return this._http
      .get<LocationCity[]>(`${this.apiEndPoint}/DueDiligence/GetCities/${stateId}`)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestById(requestId: number): Observable<DueDiligenceRequest> {
    return this._http
      .get<DueDiligenceRequest>(`${this.apiEndPoint}/DueDiligence/GetDueDiligenceByRequestId/${requestId}`)
      .pipe(
        map((response) => {
          return Object.assign(new DueDiligenceRequest(), response) as DueDiligenceRequest;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getRequestByEvaluationId(evaluationId: number): Observable<DueDiligenceRequest> {
    return this._http
      .get<DueDiligenceRequest>(`${this.apiEndPoint}/DueDiligence/GetDueDiligenceByEvaluationId/${evaluationId}`)
      .pipe(
        map((response) => {
          return Object.assign(new DueDiligenceRequest(), response) as DueDiligenceRequest;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  changeStatusRequestToInProgress(requestId: number): Observable<any> {
    const params = new HttpParams()
      .append("dueDiligenceId", requestId.toString());

    return this._http
      .get(`${this.apiEndPoint}/DueDiligence/ChangeDueDiligenceToInProgress`, { params })
      .pipe(
        map(response => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  downloadTemplate(): Observable<any> {
    const culture: Culture = Culture[this._auth.currentUserLanguage];

    return this._http
      .get(`${this.apiEndPoint}/DueDiligence/DownloadTemplate?culture=${culture}`, { responseType: 'blob' })
      .pipe(
        map(response => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  importRequest(file: ThirdPartyEvaluationFormItemFile) {
    return this._http
      .post(`${this.apiEndPoint}/DueDiligence/Import`, file)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  deleteFile(requestId: number, fileId: number) {
    const params = new HttpParams()
      .append("dueDiligenceRequesId", requestId.toString())
      .append("fileId", fileId.toString());

    return this._http
      .delete(`${this.apiEndPoint}/DueDiligence/Import`, { params })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getSlaDate(priorityId: number, providerId: number): Observable<Date> {
    const params = new HttpParams()
      .append("priorityId", priorityId.toString())
      .append("providerId", providerId.toString());

    return this._http
      .get<Date>(`${this.apiEndPoint}/DueDiligence/GetSlaDate`, { params })
      .pipe(
        map(response => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getFormDto(requestId: number): Observable<any> {
    const params = new HttpParams()
      .append("dueDiligenceId", requestId.toString());

    return this._http
      .get<any>(`${this.apiEndPoint}/DueDiligence/DueDiligenceForFormDto`, { params })
      .pipe(
        map(response => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  // Guardado parcial del Proveedor de DueDiligence sin Evaluación
  saveFormPartialNoEvaluation(requestId: number, formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/DueDiligence/ExternalProviderPartialSave/${requestId}`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  // Guardado parcial del Proveedor de DueDiligence con Evaluación
  saveFormPartialWithEvaluation(formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/ExternalDueDiligenceProviderUserPartialSave`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  // Guardado total del Proveedor de DueDiligence sin Evaluacion
  saveFormCompletedNoEvaluation(requestId: number, formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/DueDiligence/ExternalProviderSave/${requestId}`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  // Guardado total del Proveedor de DueDiligence con Evaluacion
  saveFormCompletedWithEvaluation(formAnswer: ThirdPartyEvaluationFormAnswer): Observable<any> {
    return this._http
      .post(`${this.apiEndPoint}/FormHandler/ExternalDueDiligenceProviderUserSave`, formAnswer)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }

          return _throw(this._translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }




}
